export interface LoginBlockedState {
  message: string;
  blockedUntil: string;
}

const LOGIN_BLOCKED_STORAGE_KEY = 'auth_login_blocked';
const LOGIN_BLOCKED_DESCRIPTION_PATTERN =
  /account temporarily locked due to too many failed login attempts/i;
const BLOCKED_UNTIL_PATTERN = /try again after\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)/i;

const isLoginBlockedState = (value: unknown): value is LoginBlockedState => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return typeof candidate.message === 'string' && typeof candidate.blockedUntil === 'string';
};

const isFutureTimestamp = (timestamp: string, nowMs = Date.now()) => {
  const timestampMs = Date.parse(timestamp);

  return !Number.isNaN(timestampMs) && timestampMs > nowMs;
};

export const getLoginBlockedStateFromCallback = (
  error: string | null,
  description: string | null,
  nowMs = Date.now(),
): LoginBlockedState | null => {
  if (error !== 'access_denied' || description === null) {
    return null;
  }

  if (!LOGIN_BLOCKED_DESCRIPTION_PATTERN.test(description)) {
    return null;
  }

  const blockedUntil = description.match(BLOCKED_UNTIL_PATTERN)?.[1];

  if (!blockedUntil || !isFutureTimestamp(blockedUntil, nowMs)) {
    return null;
  }

  return {
    message: description,
    blockedUntil,
  };
};

export const saveLoginBlockedState = (state: LoginBlockedState) => {
  sessionStorage.setItem(LOGIN_BLOCKED_STORAGE_KEY, JSON.stringify(state));
};

export const clearLoginBlockedState = () => {
  sessionStorage.removeItem(LOGIN_BLOCKED_STORAGE_KEY);
};

export const readLoginBlockedState = (nowMs = Date.now()): LoginBlockedState | null => {
  const storedValue = sessionStorage.getItem(LOGIN_BLOCKED_STORAGE_KEY);

  if (storedValue === null) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(storedValue);

    if (!isLoginBlockedState(parsedValue) || !isFutureTimestamp(parsedValue.blockedUntil, nowMs)) {
      clearLoginBlockedState();
      return null;
    }

    return parsedValue;
  } catch {
    clearLoginBlockedState();
    return null;
  }
};
